import { useEffect, useRef, useState } from 'react';
import { validatePassword, type PasswordValidationStatus } from 'firebase/auth';
import {
  buildMissingMessage,
  derivePasswordRequirements,
  passwordPolicyErrorMessage,
  type PasswordRequirement,
} from './passwordPolicy';

export type ValidatePasswordFn = (password: string) => Promise<PasswordValidationStatus>;

/** Used only when the live policy cannot be fetched (offline / policy not configured). */
const FALLBACK_MIN_LENGTH = 8;
const DEFAULT_DEBOUNCE_MS = 150;

export interface UsePasswordPolicyResult {
  /** Ordered checklist of the rules the policy enforces, with live met/unmet state. */
  requirements: PasswordRequirement[];
  /** Whether the password satisfies the whole policy. */
  isValid: boolean;
  /** True while the first policy check is in flight. */
  isChecking: boolean;
  /** Specific "what's missing" message, or null when valid. */
  errorMessage: string | null;
}

interface UsePasswordPolicyOptions {
  /** Injectable validator (defaults to Firebase `validatePassword`); enables testing. */
  validate?: ValidatePasswordFn;
  debounceMs?: number;
  /**
   * Minimum-length floor. Use on flows whose real enforcer is the backend (the
   * invite/activate Admin-SDK path bypasses the Firebase password policy and
   * enforces its own `min=8`), so the checklist never under-promises vs the server.
   */
  minLength?: number;
}

// Lazy import keeps the Firebase singleton (and its init side effects) out of the
// module graph for consumers that inject their own validator (e.g. unit tests).
const defaultValidate: ValidatePasswordFn = async (password) => {
  const { auth } = await import('@discovr/common');
  return validatePassword(auth, password);
};

function fallbackResult(password: string, minLength?: number): UsePasswordPolicyResult {
  const min = Math.max(FALLBACK_MIN_LENGTH, minLength ?? 0);
  const met = password.length >= min;
  return {
    requirements: [{ key: 'minLength', label: `At least ${min} characters`, met }],
    isValid: met,
    isChecking: false,
    errorMessage: met ? null : `Your password needs at least ${min} characters.`,
  };
}

/** Apply a backend min-length floor on top of the live Firebase policy result. */
function applyMinLengthFloor(
  status: PasswordValidationStatus,
  password: string,
  floor: number,
): UsePasswordPolicyResult {
  const policyMin = status.passwordPolicy.customStrengthOptions.minPasswordLength ?? 0;
  const base = derivePasswordRequirements(status);

  if (floor <= policyMin) {
    return {
      requirements: base,
      isValid: status.isValid,
      isChecking: false,
      errorMessage: passwordPolicyErrorMessage(status),
    };
  }

  const met = password.length >= floor;
  const flooredRule: PasswordRequirement = {
    key: 'minLength',
    label: `At least ${floor} characters`,
    met,
  };
  const requirements = base.some((r) => r.key === 'minLength')
    ? base.map((r) => (r.key === 'minLength' ? flooredRule : r))
    : [flooredRule, ...base];
  const isValid = status.isValid && met;

  return {
    requirements,
    isValid,
    isChecking: false,
    errorMessage: isValid ? null : buildMissingMessage(requirements, floor),
  };
}

/** Build the result for a resolved policy status (null = no live policy → fallback). */
function computeResult(
  status: PasswordValidationStatus | null,
  password: string,
  minLength?: number,
): UsePasswordPolicyResult {
  if (status === null) {
    // Validate threw (policy not configured / offline): length-only fallback is
    // the real policy in that case.
    return fallbackResult(password, minLength);
  }
  if (minLength != null) {
    return applyMinLengthFloor(status, password, minLength);
  }
  return {
    requirements: derivePasswordRequirements(status),
    isValid: status.isValid,
    isChecking: false,
    errorMessage: passwordPolicyErrorMessage(status),
  };
}

/**
 * Live password-policy validation against the project's Firebase policy. Returns
 * the requirement checklist, overall validity, and a specific error message — all
 * sourced from the configured policy so the UI never drifts from enforcement.
 */
export function usePasswordPolicy(
  password: string,
  options: UsePasswordPolicyOptions = {},
): UsePasswordPolicyResult {
  const { validate = defaultValidate, debounceMs = DEFAULT_DEBOUNCE_MS, minLength } = options;
  // The freshest resolved validation, tagged with the password it describes — so a
  // result is never reported valid for a password that has since been edited.
  const [resolved, setResolved] = useState<{
    password: string;
    status: PasswordValidationStatus | null;
  } | null>(null);
  const runId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const id = ++runId.current;
    const timer = setTimeout(() => {
      validate(password)
        .then((s) => {
          if (!cancelled && id === runId.current) setResolved({ password, status: s });
        })
        .catch(() => {
          if (!cancelled && id === runId.current) setResolved({ password, status: null });
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [password, validate, debounceMs]);

  const isFresh = resolved !== null && resolved.password === password;
  const result = computeResult(resolved?.status ?? null, password, minLength);

  // A check for the current password is still pending: keep the (stale) checklist
  // visible for continuity, but never report it as valid — hold the submit gate
  // until the live policy confirms the new value.
  if (!isFresh) {
    return { ...result, isValid: false, isChecking: true };
  }
  return result;
}
